<script setup>
import Breadcrumbs from '@/components/blog/Breadcrumbs.vue'

defineProps({
    post: { type: Object, required: true },
    related: { type: Array, default: () => [] },
    breadcrumbs: { type: Array, default: () => [] },
    neighbours: { type: Object, default: () => ({ previous: null, next: null }) },
})

const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
</script>

<template>
    <Head>
        <title>{{ post.meta_title }}</title>
        <meta
            head-key="description"
            name="description"
            :content="post.meta_description || post.excerpt || ''"
        />
        <meta
            v-if="post.meta_keywords"
            head-key="keywords"
            name="keywords"
            :content="post.meta_keywords"
        />
        <meta
            head-key="robots"
            name="robots"
            :content="post.robots"
        />
        <link
            head-key="canonical"
            rel="canonical"
            :href="post.canonical_url"
        />
        <meta
            head-key="og:title"
            property="og:title"
            :content="post.meta_title"
        />
        <meta
            head-key="og:description"
            property="og:description"
            :content="post.meta_description || post.excerpt || ''"
        />
        <meta
            head-key="og:type"
            property="og:type"
            content="article"
        />
        <meta
            v-if="post.cover_image_url"
            head-key="og:image"
            property="og:image"
            :content="post.cover_image_url"
        />
    </Head>

    <article class="bg-white pt-32 pb-20 md:pt-40 md:pb-28">
        <div class="mx-auto max-w-3xl px-6 lg:px-10">
            <Breadcrumbs :items="breadcrumbs" />

            <header class="mt-8">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium tracking-wide uppercase">
                    <Link
                        class="text-accent-600 hover:text-accent-700"
                        v-if="post.category"
                        :href="`/blog/rubrika/${post.category.slug}`"
                    >
                        {{ post.category.name }}
                    </Link>

                    <time
                        class="text-zinc-500"
                        v-if="post.published_at"
                        :datetime="post.published_at"
                    >
                        {{ formatDate(post.published_at) }}
                    </time>

                    <span class="text-zinc-400">{{ post.reading_time }} min čtení</span>
                </div>

                <h1 class="text-brand-900 mt-4 text-4xl leading-[1.1] font-extrabold tracking-tight sm:text-5xl">
                    {{ post.title }}
                </h1>

                <p
                    class="mt-6 text-lg leading-relaxed text-zinc-600"
                    v-if="post.excerpt"
                >
                    {{ post.excerpt }}
                </p>

                <p
                    class="mt-6 text-sm text-zinc-500"
                    v-if="post.author"
                >
                    Autor: <span class="text-brand-900 font-semibold">{{ post.author }}</span>
                </p>
            </header>

            <img
                class="mt-10 w-full rounded-2xl object-cover"
                v-if="post.cover_image_url"
                :src="post.cover_image_url"
                :alt="post.title"
            />

            <!-- Obsah článku — kotvy generuje ArticleContent na serveru -->
            <nav
                class="border-brand-200 bg-brand-50/60 mt-10 rounded-2xl border p-6"
                v-if="post.table_of_contents.length > 1"
                aria-label="Obsah článku"
            >
                <p class="text-brand-900 text-sm font-bold tracking-wide uppercase">Obsah článku</p>

                <ol class="mt-4 space-y-2">
                    <li
                        v-for="heading in post.table_of_contents"
                        :key="heading.id"
                        :class="heading.level === 3 ? 'ml-4' : ''"
                    >
                        <a
                            class="text-accent-600 hover:text-accent-700 text-base underline-offset-4 hover:underline"
                            :href="`#${heading.id}`"
                        >
                            {{ heading.text }}
                        </a>
                    </li>
                </ol>
            </nav>

            <!-- HTML skládá ArticleContent z bloků; styly jsou v app.css u .article-body -->
            <div
                class="article-body mt-10"
                v-html="post.body"
            />

            <nav
                class="mt-16 grid gap-4 border-t border-zinc-200 pt-8 sm:grid-cols-2"
                v-if="neighbours.previous || neighbours.next"
                aria-label="Další články"
            >
                <Link
                    class="group rounded-xl border border-zinc-200 p-5 transition-colors hover:border-zinc-300 hover:bg-zinc-50"
                    v-if="neighbours.previous"
                    :href="`/blog/${neighbours.previous.slug}`"
                >
                    <span class="text-xs font-semibold tracking-wide text-zinc-500 uppercase">← Předchozí článek</span>
                    <span class="text-brand-900 mt-2 block font-bold">{{ neighbours.previous.title }}</span>
                </Link>

                <Link
                    class="group rounded-xl border border-zinc-200 p-5 transition-colors hover:border-zinc-300 hover:bg-zinc-50 sm:col-start-2 sm:text-right"
                    v-if="neighbours.next"
                    :href="`/blog/${neighbours.next.slug}`"
                >
                    <span class="text-xs font-semibold tracking-wide text-zinc-500 uppercase"
                        >Následující článek →</span
                    >
                    <span class="text-brand-900 mt-2 block font-bold">{{ neighbours.next.title }}</span>
                </Link>
            </nav>
        </div>

        <aside
            class="mx-auto mt-20 max-w-7xl px-6 lg:px-10"
            v-if="related.length"
        >
            <h2 class="text-brand-900 text-2xl font-extrabold tracking-tight md:text-3xl">Mohlo by vás zajímat</h2>

            <div class="mt-8 grid gap-6 md:grid-cols-3 md:gap-8">
                <Link
                    class="group flex flex-col rounded-2xl border border-zinc-200 p-6 transition-shadow hover:shadow-md"
                    v-for="item in related"
                    :key="item.slug"
                    :href="`/blog/${item.slug}`"
                >
                    <h3 class="text-brand-900 text-lg font-bold tracking-tight">{{ item.title }}</h3>

                    <p
                        class="mt-3 text-base leading-relaxed text-zinc-600"
                        v-if="item.excerpt"
                    >
                        {{ item.excerpt }}
                    </p>

                    <div class="mt-6 flex-1" />

                    <span class="text-accent-600 text-sm font-semibold">Číst dál →</span>
                </Link>
            </div>
        </aside>
    </article>
</template>
